import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, QrCode, HelpCircle, MapPin, CalendarClock, Gift, Sparkles,
  CheckCircle2, X, Clock, ArrowRight, Star, Medal, Crown, Snowflake, ListChecks,
  Award, Lock, Compass, Brain, TrendingUp,
} from "lucide-react";

const TYPE_ICON = { QR_CHECKIN: QrCode, ACTIVITY: CalendarClock, TRIVIA_POLL: HelpCircle, EXPLORE: MapPin, BONUS: Gift };
const BADGE_ICON = { medal: Medal, crown: Crown, sparkles: Sparkles, compass: Compass, brain: Brain, "trending-up": TrendingUp, star: Star, award: Award, lock: Lock, trophy: Trophy };
const fmtDT = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : null);

export default function Challenge() {
  const { eventId, attendeeId } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [success, setSuccess] = useState(null); // {points}
  const [view, setView] = useState("challenges"); // challenges | leaderboard

  const load = useCallback(async () => {
    if (!eventId || !attendeeId) return;
    setLoading(true);
    try { setData(await api.gamMyChallenges(eventId, attendeeId)); }
    catch (e) { /* visibility handled below */ }
    setLoading(false);
  }, [eventId, attendeeId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="px-5 pt-20 text-cream/70 font-serif text-lg">Loading challenges…</div>;
  // Route guard: legacy challenge content renders ONLY when the server explicitly
  // reports the surface as visible (master switch AND the event's challenge
  // feature flag). Any other state — disabled feature, error, or missing data —
  // shows a clear unavailable state and loads no legacy content.
  if (!data || data.visible !== true) {
    return (
      <div className="px-5 pt-24 text-center" data-testid="attendee-challenge-unavailable">
        <Trophy className="h-10 w-10 mx-auto text-[hsl(var(--pc-gold)/0.6)]" />
        <h1 className="font-serif text-2xl text-cream mt-3">Challenges</h1>
        <p className="text-cream/60 text-sm mt-2">Challenges aren't available for this event yet. Check back soon.</p>
      </div>
    );
  }
  const p = data?.progress || { points: 0, completed_count: 0, available_count: 0, badges_earned: 0 };
  const hasRank = p.rank != null;
  const stats = [
    { label: "Points", value: p.points },
    ...(hasRank ? [{ label: "Rank", value: `#${p.rank}` }] : []),
    { label: "Completed", value: p.completed_count },
    { label: "Badges", value: p.badges_earned ?? 0 },
  ];

  return (
    <div className="px-5 pt-20 pb-8" data-testid="attendee-challenge-page">
      <header className="mb-4">
        <h1 className="font-serif text-3xl text-cream flex items-center gap-2"><Trophy className="h-7 w-7 text-[hsl(var(--pc-gold))]" /> PC Challenge</h1>
        <p className="text-cream/60 text-sm mt-1">Explore. Participate. Earn points.</p>
      </header>

      {/* My Progress */}
      <div className={`att-card grid grid-cols-${stats.length} divide-x divide-[hsl(var(--pc-line)/0.7)] mb-4`} data-testid="attendee-challenge-progress">
        {stats.map((s) => <Progress key={s.label} label={s.label} value={s.value} />)}
      </div>

      {/* Section tabs: Challenges | Leaderboard | Achievements */}
      <div className="flex gap-2 mb-5" data-testid="challenge-subnav">
        <TabButton active={view === "challenges"} onClick={() => setView("challenges")} icon={ListChecks} label="Challenges" testid="challenge-subtab-challenges" />
        <TabButton active={view === "leaderboard"} onClick={() => setView("leaderboard")} icon={Medal} label="Leaderboard" testid="challenge-subtab-leaderboard" />
        <TabButton active={view === "achievements"} onClick={() => setView("achievements")} icon={Award} label="Achievements" testid="challenge-subtab-achievements" />
      </div>

      {view === "challenges" && (
        <>
          <Section title="Available now" items={data?.live} onOpen={setOpenId} empty="No live challenges right now." testid="challenge-section-live" />
          <Section title="Upcoming" items={data?.upcoming} onOpen={setOpenId} empty="Nothing scheduled yet." testid="challenge-section-upcoming" />
          <Section title="Completed" items={data?.completed} onOpen={setOpenId} empty="You haven't completed any challenges yet." testid="challenge-section-completed" completedStyle />
        </>
      )}
      {view === "leaderboard" && <Leaderboard eventId={eventId} attendeeId={attendeeId} />}
      {view === "achievements" && <Achievements eventId={eventId} attendeeId={attendeeId} />}

      {openId && (
        <ChallengeDetail eventId={eventId} attendeeId={attendeeId} challengeId={openId}
          onClose={() => setOpenId(null)}
          onCompleted={(pts, badges) => { setOpenId(null); setSuccess({ points: pts, badges: badges || [] }); load(); }} />
      )}
      {success && <SuccessOverlay points={success.points} badges={success.badges} onClose={() => setSuccess(null)} />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${active
        ? "bg-[hsl(var(--pc-gold)/0.16)] border-[hsl(var(--pc-gold)/0.55)] text-[hsl(var(--pc-gold))]"
        : "border-[hsl(var(--pc-line))] text-cream/60 hover:text-cream/90"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function Progress({ label, value }) {
  return (
    <div className="py-3 text-center">
      <div className="font-serif text-2xl text-[hsl(var(--pc-gold))]" data-testid={`progress-${label.toLowerCase()}`}>{value ?? 0}</div>
      <div className="text-[11px] uppercase tracking-wide text-cream/55">{label}</div>
    </div>
  );
}

// ---- Leaderboard (attendee) ------------------------------------------------
function Leaderboard({ eventId, attendeeId }) {
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.gamLeaderboard(eventId, attendeeId)
      .then((r) => { if (ok) setLb(r); })
      .catch(() => { if (ok) setLb({ visible: false }); })
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [eventId, attendeeId]);

  if (loading) return <div className="att-card text-cream/60 text-sm py-6 text-center" data-testid="leaderboard-loading">Loading leaderboard…</div>;
  if (!lb || lb.visible === false) {
    return (
      <div className="att-card text-center py-8" data-testid="leaderboard-hidden">
        <Medal className="h-8 w-8 mx-auto text-[hsl(var(--pc-gold)/0.5)]" />
        <p className="text-cream/60 text-sm mt-2">The leaderboard isn't available right now.</p>
      </div>
    );
  }
  const rows = lb.top || [];
  const me = lb.me; // present only when outside the visible list

  return (
    <section data-testid="attendee-leaderboard">
      {lb.frozen && (
        <div className="att-card mb-3 flex items-center gap-2 py-2.5 border-[hsl(var(--pc-gold)/0.4)]" data-testid="leaderboard-frozen-banner">
          <Snowflake className="h-4 w-4 text-[hsl(var(--pc-gold))]" />
          <span className="text-xs text-cream/80">Final results{lb.post_freeze_adjustment ? " · a post-freeze adjustment was recorded" : ""}</span>
        </div>
      )}
      {lb.mode === "MY_RANK_ONLY" ? (
        me ? <RankRow r={me} showPoints={lb.show_points} showRank={lb.show_rank} highlight /> :
          <div className="att-card text-cream/60 text-sm py-6 text-center">No ranking yet — complete a challenge to get on the board.</div>
      ) : (
        <>
          {rows.length === 0 ? (
            <div className="att-card text-cream/60 text-sm py-6 text-center" data-testid="leaderboard-empty">No one is on the board yet. Be the first!</div>
          ) : (
            <div className="space-y-2" data-testid="leaderboard-list">
              {rows.map((r) => <RankRow key={r.attendee_id} r={r} showPoints={lb.show_points} showRank={lb.show_rank} highlight={r.is_you} />)}
            </div>
          )}
          {me && (
            <>
              <div className="text-center text-cream/40 text-xs my-2">• • •</div>
              <RankRow r={me} showPoints={lb.show_points} showRank={lb.show_rank} highlight />
            </>
          )}
        </>
      )}
    </section>
  );
}

function RankRow({ r, showPoints, showRank, highlight }) {
  const medal = r.rank === 1 ? "text-[hsl(45_90%_60%)]" : r.rank === 2 ? "text-[hsl(210_10%_75%)]" : r.rank === 3 ? "text-[hsl(28_60%_55%)]" : "text-cream/50";
  const initials = (r.display_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div data-testid={highlight ? "leaderboard-you-row" : "leaderboard-row"}
      className={`att-card flex items-center gap-3 py-2.5 ${highlight ? "border-[hsl(var(--pc-gold)/0.6)] bg-[hsl(var(--pc-gold)/0.08)]" : ""}`}>
      <div className={`w-8 text-center font-serif text-lg font-bold ${medal}`} aria-label={showRank ? `Rank ${r.rank}` : "Rank hidden"}>
        {showRank ? (r.rank != null ? `#${r.rank}` : "—") : ""}
      </div>
      {r.rank <= 3 && showRank ? <Crown className={`h-4 w-4 ${medal}`} /> : null}
      <div className="h-8 w-8 rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] grid place-items-center text-xs font-semibold shrink-0">{initials}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-cream truncate">{r.display_name}{highlight && <span className="text-[hsl(var(--pc-gold))] text-xs font-bold"> · You</span>}</div>
        <div className="text-[11px] text-cream/50">{r.completions} {r.completions === 1 ? "challenge" : "challenges"}</div>
      </div>
      {showPoints && r.points != null && (
        <div className="text-right shrink-0">
          <div className="font-bold text-[hsl(var(--pc-gold))]">{r.points}</div>
          <div className="text-[10px] uppercase tracking-wide text-cream/45">pts</div>
        </div>
      )}
    </div>
  );
}


function Section({ title, items, onOpen, empty, testid, completedStyle }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <h2 className="font-serif text-lg text-cream/90 mb-2">{title}</h2>
      {!items || items.length === 0 ? (
        <div className="att-card text-cream/50 text-sm py-4 text-center">{empty}</div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => <Card key={c.challenge_id} c={c} onOpen={onOpen} completedStyle={completedStyle} />)}
        </div>
      )}
    </section>
  );
}

function Card({ c, onOpen, completedStyle }) {
  const Icon = c.teaser ? Sparkles : (TYPE_ICON[c.type] || Trophy);
  return (
    <button data-testid="attendee-challenge-card" onClick={() => !c.teaser && onOpen(c.challenge_id)}
      className={`att-card w-full text-left flex items-center gap-3 ${c.teaser ? "opacity-90 cursor-default" : "hover:border-[hsl(var(--pc-gold)/0.5)]"}`}>
      <div className="h-11 w-11 rounded-xl bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] grid place-items-center shrink-0"><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-cream flex items-center gap-1.5 truncate">{c.title}{c.featured && <Star className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" />}</div>
        {c.teaser ? (
          <div className="text-xs text-cream/60 flex items-center gap-1"><Clock className="h-3 w-3" /> Unlocks {fmtDT(c.reveal_at)}</div>
        ) : (
          <div className="text-xs text-cream/60 truncate">{c.short_description}</div>
        )}
        <div className="flex items-center gap-2 mt-1">
          {c.points != null && <span className="text-[11px] font-bold text-[hsl(var(--pc-gold))]">+{c.points} pts</span>}
          {c.completed ? (
            <span className="text-[11px] font-bold text-[hsl(var(--pc-mint,150_40%_55%))] inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Completed</span>
          ) : c.runtime_state === "LIVE" ? <span className="text-[11px] text-cream/55">Available now</span>
            : c.runtime_state === "SCHEDULED" ? <span className="text-[11px] text-cream/55">Scheduled</span> : null}
        </div>
      </div>
      {!c.teaser && !completedStyle && <ArrowRight className="h-4 w-4 text-cream/40" />}
    </button>
  );
}

function ChallengeDetail({ eventId, attendeeId, challengeId, onClose, onCompleted }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [triviaState, setTriviaState] = useState(null);
  const [code, setCode] = useState("");
  const [tokenEntry, setTokenEntry] = useState("");
  const [showScan, setShowScan] = useState(false);

  const load = useCallback(async () => {
    try { setD(await api.gamMyChallenge(eventId, attendeeId, challengeId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unavailable"); onClose(); }
  }, [eventId, attendeeId, challengeId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const comp = d.completion || {};
  const key = () => `${challengeId}:${attendeeId}:${Date.now()}`;

  const doScan = async () => {
    setBusy(true);
    try {
      const r = await api.gamScan(eventId, { token: tokenEntry.trim(), attendee_id: attendeeId, idempotency_key: `scan:${challengeId}:${attendeeId}` });
      if (r.status === "completed") onCompleted(r.points_awarded, r.newly_earned_badges);
      else { toast.info(`Already completed (+${r.points_awarded ?? 0})`); onClose(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    setBusy(false);
  };
  const doTrivia = async () => {
    if (answer == null) return;
    setBusy(true);
    try {
      const r = await api.gamTrivia(eventId, attendeeId, challengeId, { option_index: answer, idempotency_key: `trivia:${challengeId}:${attendeeId}:${Date.now()}` });
      setTriviaState(r);
      if (r.completed) { onCompleted(r.points_awarded, r.newly_earned_badges); }
      else if (r.attempts_remaining === 0) toast.error("No attempts remaining");
      else toast.error("Not quite — try again");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const doPoll = async () => {
    setBusy(true);
    try {
      const r = await api.gamPoll(eventId, attendeeId, challengeId, { option_index: answer ?? 0, idempotency_key: `poll:${challengeId}:${attendeeId}` });
      if (r.completed) onCompleted(r.points_awarded, r.newly_earned_badges);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const doCode = async () => {
    setBusy(true);
    try {
      const r = await api.gamExploreCode(eventId, attendeeId, challengeId, { code, idempotency_key: `code:${challengeId}:${attendeeId}` });
      if (r.status === "completed") onCompleted(r.points_awarded, r.newly_earned_badges); else onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Incorrect code"); }
    setBusy(false);
  };

  const Icon = TYPE_ICON[d.type] || Trophy;
  const isTrivia = d.type === "TRIVIA_POLL";
  const mode = comp.mode || "TRIVIA";

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="attendee-challenge-detail">
      <div className="w-full max-w-[30rem] max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl att-glass border border-[hsl(var(--pc-line))] p-5 text-cream">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2"><div className="h-10 w-10 rounded-xl bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] grid place-items-center"><Icon className="h-5 w-5" /></div>
            <div><h3 className="font-serif text-xl">{d.title}</h3>{d.points != null && <div className="text-xs font-bold text-[hsl(var(--pc-gold))]">+{d.points} points</div>}</div></div>
          <button data-testid="challenge-detail-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        {d.short_description && <p className="text-sm text-cream/85 mt-3">{d.short_description}</p>}
        {d.instructions && <p className="text-xs text-cream/60 mt-1">{d.instructions}</p>}
        {(d.start_at || d.end_at) && <p className="text-[11px] text-cream/50 mt-2">Available {fmtDT(d.start_at) || "now"}{d.end_at ? ` → ${fmtDT(d.end_at)}` : ""}</p>}
        {d.type === "ACTIVITY" && d.activity && <p className="text-xs text-cream/70 mt-2">Linked to: {d.activity.title} ({d.activity.date})</p>}

        {d.completed ? (
          <div className="mt-4 rounded-xl bg-[hsl(var(--pc-gold)/0.12)] p-3 text-center" data-testid="challenge-detail-completed">
            <CheckCircle2 className="h-6 w-6 mx-auto text-[hsl(var(--pc-gold))]" />
            <div className="font-semibold mt-1">Completed ✓</div>
            {comp && d.points_awarded != null && <div className="text-xs text-cream/70">+{d.points_awarded} points earned</div>}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {/* QR */}
            {d.type === "QR_CHECKIN" && (
              <>
                {!showScan ? (
                  <button data-testid="challenge-cta-scan" onClick={() => setShowScan(true)} className="w-full att-btn-gold py-2.5 rounded-full font-semibold">Scan QR</button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-cream/60">Point your phone camera at the challenge QR, or paste the scan link/code below.</p>
                    <input data-testid="challenge-token-input" value={tokenEntry} onChange={(e) => setTokenEntry(e.target.value.split("/challenge/scan/").pop())} placeholder="Paste scan link or code" className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-sm text-cream placeholder:text-cream/40" />
                    <button data-testid="challenge-submit-scan" disabled={busy || !tokenEntry} onClick={doScan} className="w-full att-btn-gold py-2.5 rounded-full font-semibold disabled:opacity-50">Complete</button>
                  </div>
                )}
              </>
            )}
            {/* Trivia / Poll */}
            {isTrivia && (
              <div data-testid="challenge-trivia">
                <p className="font-medium text-cream mb-2">{comp.question}</p>
                <div className="space-y-2">
                  {(comp.options || []).map((opt, i) => (
                    <button key={i} data-testid={`trivia-option-${i}`} onClick={() => setAnswer(i)}
                      className={`w-full text-left rounded-xl px-3 py-2.5 text-sm border transition-colors ${answer === i ? "border-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.12)]" : "border-white/15 hover:border-white/30"}`}>{opt}</button>
                  ))}
                </div>
                {triviaState && !triviaState.completed && (
                  <p className="text-xs mt-2 text-cream/70" data-testid="trivia-feedback">
                    {triviaState.correct === false ? "Incorrect. " : ""}Attempts remaining: {triviaState.attempts_remaining}
                    {triviaState.correct_option_index != null ? ` · Correct answer: ${(comp.options || [])[triviaState.correct_option_index]}` : ""}
                  </p>
                )}
                <button data-testid="challenge-submit-trivia" disabled={busy || answer == null || (triviaState && triviaState.attempts_remaining === 0)}
                  onClick={mode === "POLL" ? doPoll : doTrivia}
                  className="mt-3 w-full att-btn-gold py-2.5 rounded-full font-semibold disabled:opacity-50">{mode === "POLL" ? "Take Poll" : "Answer Challenge"}</button>
              </div>
            )}
            {/* Explore */}
            {d.type === "EXPLORE" && comp.method === "code" && (
              <div className="space-y-2">
                <input data-testid="challenge-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter secret code" className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-sm text-cream placeholder:text-cream/40" />
                <button data-testid="challenge-submit-code" disabled={busy || !code} onClick={doCode} className="w-full att-btn-gold py-2.5 rounded-full font-semibold disabled:opacity-50">Submit code</button>
              </div>
            )}
            {d.type === "EXPLORE" && comp.method === "qr" && (
              <div className="space-y-2">
                <input data-testid="challenge-token-input" value={tokenEntry} onChange={(e) => setTokenEntry(e.target.value.split("/challenge/scan/").pop())} placeholder="Paste scan link or code" className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-sm text-cream placeholder:text-cream/40" />
                <button data-testid="challenge-submit-scan" disabled={busy || !tokenEntry} onClick={doScan} className="w-full att-btn-gold py-2.5 rounded-full font-semibold disabled:opacity-50">Complete</button>
              </div>
            )}
            {d.type === "EXPLORE" && comp.method === "manual" && <p className="text-xs text-cream/60">A member of the events team will mark this complete.</p>}
            {d.type === "ACTIVITY" && <p className="text-xs text-cream/60">Complete by scanning the activity QR or via the events team.</p>}
            {d.type === "BONUS" && <p className="text-xs text-cream/60">This bonus is awarded by the events team.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessOverlay({ points, badges = [], onClose }) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/70 backdrop-blur-sm" data-testid="challenge-success" onClick={onClose}>
      <div className="text-center px-8">
        <div className="h-20 w-20 mx-auto rounded-full bg-[hsl(var(--pc-gold)/0.18)] grid place-items-center animate-[pulse_1.2s_ease-in-out]"><CheckCircle2 className="h-10 w-10 text-[hsl(var(--pc-gold))]" /></div>
        <h2 className="font-serif text-3xl text-cream mt-4">Challenge Complete</h2>
        {points != null && <div className="text-[hsl(var(--pc-gold))] font-bold text-xl mt-1">+{points} points</div>}
        {badges.length > 0 && (
          <div className="mt-5" data-testid="challenge-success-badges">
            <div className="text-xs uppercase tracking-wide text-cream/60">{badges.length > 1 ? "Achievements unlocked" : "Achievement unlocked"}</div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {badges.map((b) => {
                const Bi = BADGE_ICON[b.icon] || Medal;
                return (
                  <div key={b.badge_id} className="flex flex-col items-center" data-testid="success-badge">
                    <div className="h-14 w-14 rounded-full bg-gradient-to-b from-[hsl(45_60%_55%/0.35)] to-[hsl(var(--pc-gold)/0.12)] border border-[hsl(var(--pc-gold)/0.5)] grid place-items-center"><Bi className="h-6 w-6 text-[hsl(var(--pc-gold))]" /></div>
                    <span className="text-cream text-sm font-semibold mt-1">{b.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <button data-testid="challenge-success-progress" className="mt-5 att-btn-gold px-5 py-2.5 rounded-full font-semibold">View My Progress</button>
      </div>
    </div>
  );
}

// ---- Achievements (attendee) ----------------------------------------------
function BadgeMedal({ icon, earned, locked, mystery }) {
  const Bi = mystery ? Lock : (BADGE_ICON[icon] || Medal);
  const cls = earned
    ? "from-[hsl(45_60%_55%/0.4)] to-[hsl(var(--pc-gold)/0.15)] border-[hsl(var(--pc-gold)/0.6)] text-[hsl(var(--pc-gold))]"
    : mystery
      ? "from-[hsl(var(--pc-teal,190_40%_20%)/0.5)] to-black/10 border-dashed border-[hsl(var(--pc-line))] text-cream/40"
      : "from-white/5 to-transparent border-[hsl(var(--pc-line))] text-cream/45";
  return (
    <div className={`h-16 w-16 rounded-full grid place-items-center bg-gradient-to-b border ${cls} ${earned ? "" : "grayscale-[30%]"}`}>
      <Bi className="h-7 w-7" />
    </div>
  );
}

function Achievements({ eventId, attendeeId }) {
  const [ach, setAch] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.gamAchievements(eventId, attendeeId)
      .then((r) => ok && setAch(r))
      .catch(() => ok && setAch({ visible: false }))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [eventId, attendeeId]);

  if (loading) return <div className="att-card text-cream/60 text-sm py-6 text-center" data-testid="achievements-loading">Loading achievements…</div>;
  if (!ach || ach.visible === false) {
    return <div className="att-card text-center py-8" data-testid="achievements-unavailable"><Award className="h-8 w-8 mx-auto text-[hsl(var(--pc-gold)/0.5)]" /><p className="text-cream/60 text-sm mt-2">Achievements aren't available yet.</p></div>;
  }
  const { earned = [], locked = [], mystery = [] } = ach;
  const nothing = earned.length + locked.length + mystery.length === 0;
  if (nothing) return <div className="att-card text-center py-8 text-cream/60 text-sm" data-testid="achievements-empty">No achievements configured yet.</div>;

  return (
    <section data-testid="attendee-achievements" className="space-y-6">
      {earned.length > 0 && (
        <div data-testid="achievements-earned">
          <h3 className="font-serif text-lg text-cream mb-3">Earned <span className="text-[hsl(var(--pc-gold))]">({earned.length})</span></h3>
          <div className="grid grid-cols-3 gap-3">
            {earned.map((b) => (
              <div key={b.badge_id} className="att-card flex flex-col items-center text-center py-4" data-testid="badge-earned">
                <BadgeMedal icon={b.icon} earned />
                <span className="text-cream text-sm font-semibold mt-2 leading-tight">{b.name}</span>
                {b.description && <span className="text-[11px] text-cream/55 mt-0.5 leading-tight">{b.description}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {locked.length > 0 && (
        <div data-testid="achievements-locked">
          <h3 className="font-serif text-lg text-cream mb-3">In progress</h3>
          <div className="grid grid-cols-3 gap-3">
            {locked.map((b) => (
              <div key={b.badge_id} className="att-card flex flex-col items-center text-center py-4" data-testid="badge-locked">
                <BadgeMedal icon={b.icon} />
                <span className="text-cream/80 text-sm font-semibold mt-2 leading-tight">{b.name}</span>
                {b.progress ? (
                  <>
                    <span className="text-[11px] text-[hsl(var(--pc-gold))] mt-1 font-semibold">{b.progress.current} / {b.progress.target}</span>
                    <div className="w-full h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-full bg-[hsl(var(--pc-gold))]" style={{ width: `${Math.min(100, (b.progress.current / b.progress.target) * 100)}%` }} /></div>
                  </>
                ) : (
                  <span className="text-[11px] text-cream/50 mt-1 leading-tight">{b.hint || "Locked"}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {mystery.length > 0 && (
        <div data-testid="achievements-mystery">
          <h3 className="font-serif text-lg text-cream mb-3">Mystery</h3>
          <div className="grid grid-cols-3 gap-3">
            {mystery.map((b) => (
              <div key={b.badge_id} className="att-card flex flex-col items-center text-center py-4" data-testid="badge-mystery">
                <BadgeMedal mystery />
                <span className="text-cream/50 text-sm font-semibold mt-2">???</span>
                <span className="text-[11px] text-cream/40 mt-0.5">Mystery Badge</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
